import { Check, X } from 'lucide-react';
import { pricingPlans } from '@/mocks/homeData';
import { useNavigate } from 'react-router-dom';

export default function PricingSection() {
  const navigate = useNavigate();

  const handleCtaClick = (link: string) => {
    if (link.startsWith('#')) {
      const el = document.querySelector(link);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(link);
    }
  };

  return (
    <section id="pricing" className="bg-gray-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-xs font-semibold tracking-widest text-gold-600 uppercase">Pricing</span>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-navy-600">
              Plans That Grow With You
            </h2>
            <p className="mt-4 text-base md:text-lg text-gray-600">
              Start free for 14 days. No credit card required. Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-6 md:p-8 transition-all hover:shadow-lg ${
                  plan.highlighted
                    ? 'bg-navy-600 text-white shadow-xl scale-[1.02]'
                    : 'bg-white text-navy-600 border border-gray-200'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 right-6 bg-gold-500 text-navy-700 text-xs font-bold px-3 py-1 rounded-full">
                    {plan.badge}
                  </div>
                )}

                <h3 className={`text-xl font-bold ${plan.highlighted ? 'text-white' : 'text-navy-600'}`}>
                  {plan.name}
                </h3>

                <div className="mt-4">
                  <span className="text-sm">₹</span>
                  <span className={`text-4xl md:text-5xl font-bold ${plan.highlighted ? 'text-gold-400' : 'text-navy-600'}`}>
                    {plan.price}
                  </span>
                  <span className={`text-sm ml-1 ${plan.highlighted ? 'text-white/70' : 'text-gray-500'}`}>/ month</span>
                </div>

                <p className={`mt-1 text-sm ${plan.highlighted ? 'text-white/60' : 'text-gray-400'}`}>
                  {plan.billingNote}
                </p>

                <p className={`mt-3 text-sm ${plan.highlighted ? 'text-white/80' : 'text-gray-500'}`}>
                  {plan.for}
                </p>

                <div className={`mt-4 pt-4 border-t ${plan.highlighted ? 'border-white/20' : 'border-gray-100'}`}>
                  <p className={`text-sm font-medium mb-2 ${plan.highlighted ? 'text-white/90' : 'text-navy-600'}`}>
                    {plan.maxCounselors}
                  </p>
                  <p className={`text-sm ${plan.highlighted ? 'text-white/70' : 'text-gray-500'}`}>
                    {plan.maxStudents}
                  </p>
                </div>

                <div className="mt-4 space-y-2">
                  {plan.includes.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <Check className={`w-4 h-4 flex-shrink-0 ${plan.highlighted ? 'text-gold-400' : 'text-green-500'}`} />
                      <span className={`text-sm ${plan.highlighted ? 'text-white/80' : 'text-gray-600'}`}>{item}</span>
                    </div>
                  ))}
                  {plan.excludes.map((item) => (
                    <div key={item} className="flex items-center gap-2 opacity-50">
                      <X className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span className={`text-sm ${plan.highlighted ? 'text-white/50' : 'text-gray-400'}`}>{item}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleCtaClick(plan.ctaLink)}
                  className={`mt-6 w-full py-3 rounded-md font-semibold text-sm transition-all cursor-pointer ${
                    plan.highlighted
                      ? 'bg-gold-500 text-navy-700 hover:bg-gold-400'
                      : 'bg-navy-600 text-white hover:bg-navy-700'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <p className="mt-10 text-center text-sm text-gray-500">
            Have questions about pricing? We are here to help.{" "}
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-gold-600 hover:text-gold-500 font-medium cursor-pointer"
            >
              Chat with us →
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}